// ═══════════════════════════════════════════════════════════════════════════════
// HOMEPAGE CONTENT — admin-managed dynamic blocks on the storefront homepage.
// Each section here writes to a WebsiteSetting key that the storefront reads
// with a sane hardcoded default fallback (see HomePage.tsx DEFAULT_*).
//
//   - homepage_promo_cards      → 7 promo banner cards
//   - homepage_energy_cards     → 3+ "energy" highlight cards
//   - homepage_top_sellers      → vendor showcase row
//   - homepage_section_headers  → per-section titles/badges/subtitles
//
// No new schema is needed; the existing WebsiteSetting (key + JSON value) is
// flexible enough. The forms below are intentionally tabular / row-based so
// large arrays stay readable and admins can reorder by editing the position.
// ═══════════════════════════════════════════════════════════════════════════════
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cms } from '@terra/contracts';
import { Button, Spinner } from '@terra/ui';

import { LivePreviewPanel } from '../components/LivePreviewPanel';
import { api } from '../lib/api';
import { usePreviewStore } from '../stores/previewStore';

// ── Shared types imported from @terra/contracts ─────────────────────────────────

type PromoCardData = cms.PromoCardData;
type EnergyCardData = cms.EnergyCardData;
type TopSellerData = cms.TopSellerData;
type SectionHeadersData = cms.SectionHeadersData;

const { THEME_OPTIONS } = cms;

// ── Reusable bits ───────────────────────────────────────────────────────────────

function genId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`space-y-1 ${className ?? ''}`}>
      <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </label>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <div className="space-y-1">
      <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </label>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
      />
    </div>
  );
}

/** Upload-to-Cloudinary helper. Same pattern as websiteSetup.tsx / HeaderPage. */
function ImageUrlField({
  label,
  value,
  onChange,
  folder,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  folder: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(f: File) {
    setUploading(true);
    try {
      const sig = await api.files.getUploadSignature({ folder });
      const fd = new FormData();
      fd.append('file', f);
      fd.append('api_key', sig.apiKey);
      fd.append('timestamp', String(sig.timestamp));
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
        method: 'POST',
        body: fd,
      });
      const out = (await res.json()) as { secure_url?: string };
      if (out.secure_url) onChange(out.secure_url);
    } catch (err) {
      console.error(`${label} upload failed`, err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-1">
      <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          value={value}
          placeholder="https://… or upload →"
          onChange={(e) => onChange(e.target.value)}
          className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 flex-1 rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-9 shrink-0 items-center gap-1 rounded-lg px-3 text-[12px] font-bold transition-colors disabled:opacity-50"
        >
          {uploading ? <Spinner size="sm" /> : 'Upload'}
        </button>
      </div>
      {value && (
        <img
          src={value}
          alt=""
          className="border-border/60 mt-2 h-16 w-auto rounded-lg border object-cover"
        />
      )}
    </div>
  );
}

function SectionShell({
  title,
  description,
  saving,
  onSave,
  children,
  onAdd,
  addLabel,
}: {
  title: string;
  description?: string;
  saving: boolean;
  onSave: () => void;
  children: React.ReactNode;
  onAdd?: () => void;
  addLabel?: string;
}) {
  return (
    <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
      <div className="border-border/60 border-b px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-ink text-[15px] font-bold">{title}</h2>
            {description && <p className="text-ink-muted mt-1 text-[12px]">{description}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onAdd && (
              <button
                type="button"
                onClick={onAdd}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors"
              >
                <Plus className="h-4 w-4" /> {addLabel ?? 'Add'}
              </button>
            )}
            <Button loading={saving} onClick={onSave}>
              <Save className="mr-1.5 h-4 w-4" /> Save
            </Button>
          </div>
        </div>
      </div>
      <div className="space-y-3 p-5">{children}</div>
    </div>
  );
}

// ── Section 1: Promo Cards ──────────────────────────────────────────────────────

function PromoCardsSection() {
  const qc = useQueryClient();
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_promo_cards'],
    queryFn: () => api.cms.getSetting('homepage_promo_cards'),
  });

  const [cards, setCards] = useState<PromoCardData[]>([]);
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (!setting || inited) return;
    const raw = (setting.value as PromoCardData[] | null) ?? [];
    setCards(Array.isArray(raw) ? raw : []);
    setInited(true);
  }, [setting, inited]);

  const upsert = useMutation({
    mutationFn: () => api.cms.upsertSetting('homepage_promo_cards', cards),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_promo_cards'] });
      void qc.invalidateQueries({ queryKey: ['website-public-setting', 'homepage_promo_cards'] });
      usePreviewStore.getState().bump();
    },
  });

  function update(idx: number, patch: Partial<PromoCardData>) {
    setCards((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }
  function remove(idx: number) {
    setCards((prev) => prev.filter((_, i) => i !== idx));
  }
  function addNew() {
    setCards((prev) => [
      ...prev,
      {
        id: genId('promo'),
        href: '/search',
        themeClass: 'theme-spice',
        imageUrl: '',
        imageAlt: '',
        badge: '',
        title: '',
        description: '',
        ctaText: 'Shop Now',
      },
    ]);
  }

  if (isLoading) return <Spinner size="lg" />;

  return (
    <SectionShell
      title="Promo Cards"
      description="The colored gradient cards on the homepage. Order in this list = order on the page."
      saving={upsert.isPending}
      onSave={() => upsert.mutate()}
      onAdd={addNew}
      addLabel="Add Card"
    >
      {cards.length === 0 ? (
        <p className="text-ink-muted text-[13px]">
          No promo cards configured. The storefront will fall back to its built-in defaults.
        </p>
      ) : (
        cards.map((card, idx) => (
          <div
            key={card.id ?? idx}
            className="border-border/60 bg-surface-subtle/20 group relative space-y-3 rounded-xl border p-4"
          >
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-md text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
              aria-label="Remove card"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div className="grid gap-3 md:grid-cols-2">
              <TextField
                label="Badge"
                value={card.badge}
                onChange={(v) => update(idx, { badge: v })}
                placeholder="Premium Masala"
              />
              <TextField
                label="Title"
                value={card.title}
                onChange={(v) => update(idx, { title: v })}
                placeholder="Pure & Aromatic"
              />
              <TextField
                label="Description"
                value={card.description}
                onChange={(v) => update(idx, { description: v })}
                placeholder="Hand-ground • 100% Organic"
              />
              <TextField
                label="CTA Text"
                value={card.ctaText}
                onChange={(v) => update(idx, { ctaText: v })}
                placeholder="Explore Range"
              />
              <TextField
                label="Link (href)"
                value={card.href}
                onChange={(v) => update(idx, { href: v })}
                placeholder="/search?q=spices"
              />
              <div className="space-y-1">
                <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                  Theme
                </label>
                <select
                  value={card.themeClass}
                  onChange={(e) => update(idx, { themeClass: e.target.value })}
                  className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none"
                >
                  {THEME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <TextField
                label="Image alt text"
                value={card.imageAlt}
                onChange={(v) => update(idx, { imageAlt: v })}
                placeholder="Spices"
              />
              <TextField
                label="Object position (optional)"
                value={card.objectPosition ?? ''}
                onChange={(v) => update(idx, { objectPosition: v || undefined })}
                placeholder="center / center 40%"
              />
            </div>
            <ImageUrlField
              label="Background image"
              value={card.imageUrl}
              onChange={(v) => update(idx, { imageUrl: v })}
              folder="homepage/promo"
            />
          </div>
        ))
      )}
    </SectionShell>
  );
}

// ── Section 2: Energy Cards ─────────────────────────────────────────────────────

function EnergyCardsSection() {
  const qc = useQueryClient();
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_energy_cards'],
    queryFn: () => api.cms.getSetting('homepage_energy_cards'),
  });
  const [rows, setRows] = useState<EnergyCardData[]>([]);
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (!setting || inited) return;
    setRows(Array.isArray(setting.value) ? (setting.value as EnergyCardData[]) : []);
    setInited(true);
  }, [setting, inited]);

  const upsert = useMutation({
    mutationFn: () => api.cms.upsertSetting('homepage_energy_cards', rows),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_energy_cards'] });
      void qc.invalidateQueries({ queryKey: ['website-public-setting', 'homepage_energy_cards'] });
      usePreviewStore.getState().bump();
    },
  });

  function update(idx: number, patch: Partial<EnergyCardData>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function remove(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  if (isLoading) return <Spinner size="lg" />;

  return (
    <SectionShell
      title="Energy Cards"
      description="The 3-card highlight strip below the promo banners (marketplace strength / B2B / GST etc.)."
      saving={upsert.isPending}
      onSave={() => upsert.mutate()}
      onAdd={() =>
        setRows((p) => [...p, { id: genId('energy'), title: '', body: '', icon: '', badge: '' }])
      }
      addLabel="Add Card"
    >
      {rows.length === 0 ? (
        <p className="text-ink-muted text-[13px]">
          No energy cards configured. The storefront will fall back to defaults.
        </p>
      ) : (
        rows.map((row, idx) => (
          <div
            key={row.id ?? idx}
            className="border-border/60 bg-surface-subtle/20 relative space-y-3 rounded-xl border p-4"
          >
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-md text-red-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div className="grid gap-3 md:grid-cols-2">
              <TextField
                label="Title"
                value={row.title}
                onChange={(v) => update(idx, { title: v })}
              />
              <TextField
                label="Badge (optional)"
                value={row.badge ?? ''}
                onChange={(v) => update(idx, { badge: v || undefined })}
                placeholder="Scale Strength"
              />
              <TextField
                label="Icon (Font Awesome class, optional)"
                value={row.icon ?? ''}
                onChange={(v) => update(idx, { icon: v || undefined })}
                placeholder="fa-shop"
                className="md:col-span-1"
              />
              <TextField
                label="Body"
                value={row.body}
                onChange={(v) => update(idx, { body: v })}
                className="md:col-span-2"
              />
            </div>
          </div>
        ))
      )}
    </SectionShell>
  );
}

// ── Section 3: Top Sellers ──────────────────────────────────────────────────────

function TopSellersSection() {
  const qc = useQueryClient();
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_top_sellers'],
    queryFn: () => api.cms.getSetting('homepage_top_sellers'),
  });
  const [rows, setRows] = useState<TopSellerData[]>([]);
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (!setting || inited) return;
    setRows(Array.isArray(setting.value) ? (setting.value as TopSellerData[]) : []);
    setInited(true);
  }, [setting, inited]);

  const upsert = useMutation({
    mutationFn: () => api.cms.upsertSetting('homepage_top_sellers', rows),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_top_sellers'] });
      void qc.invalidateQueries({ queryKey: ['website-public-setting', 'homepage_top_sellers'] });
      usePreviewStore.getState().bump();
    },
  });

  function update(idx: number, patch: Partial<TopSellerData>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  if (isLoading) return <Spinner size="lg" />;

  return (
    <SectionShell
      title="Top Sellers"
      description="Vendor showcase row. Tag = 2-letter avatar text. Color is a hex (#0ea5e9)."
      saving={upsert.isPending}
      onSave={() => upsert.mutate()}
      onAdd={() =>
        setRows((p) => [
          ...p,
          {
            id: genId('seller'),
            name: '',
            tag: '',
            color: '#0ea5e9',
            rating: 4.5,
            reviews: 0,
            href: '/search',
          },
        ])
      }
      addLabel="Add Seller"
    >
      {rows.length === 0 ? (
        <p className="text-ink-muted text-[13px]">
          No top sellers configured. The storefront will fall back to defaults.
        </p>
      ) : (
        rows.map((row, idx) => (
          <div
            key={row.id ?? idx}
            className="border-border/60 bg-surface-subtle/20 relative grid gap-3 rounded-xl border p-4 md:grid-cols-3"
          >
            <button
              type="button"
              onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}
              className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-md text-red-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Remove"
            >
              <X className="h-4 w-4" />
            </button>
            <TextField
              label="Name"
              value={row.name}
              onChange={(v) => update(idx, { name: v })}
              placeholder="Aarna Organics"
            />
            <TextField
              label="Tag (avatar letters)"
              value={row.tag}
              onChange={(v) => update(idx, { tag: v.slice(0, 3) })}
              placeholder="AO"
            />
            <div className="space-y-1">
              <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                Color (hex)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={row.color}
                  onChange={(e) => update(idx, { color: e.target.value })}
                  className="border-border/60 h-9 w-9 shrink-0 cursor-pointer rounded-lg border bg-transparent p-0"
                />
                <input
                  value={row.color}
                  onChange={(e) => update(idx, { color: e.target.value })}
                  className="bg-surface border-border/60 h-9 flex-1 rounded-lg border px-3 font-mono text-[12px] outline-none"
                />
              </div>
            </div>
            <NumberField
              label="Rating (0-5)"
              value={row.rating}
              onChange={(v) => update(idx, { rating: v })}
              step={0.1}
            />
            <NumberField
              label="Reviews"
              value={row.reviews}
              onChange={(v) => update(idx, { reviews: v })}
            />
            <TextField
              label="Link (href)"
              value={row.href ?? ''}
              onChange={(v) => update(idx, { href: v })}
              placeholder="/seller/aarna-organics"
            />
          </div>
        ))
      )}
    </SectionShell>
  );
}

// ── Section 4: Section Headers ──────────────────────────────────────────────────

// Local state shape for the form — every nested group is guaranteed present
// (we always seed with `{}`), so we can drop the outer `| undefined` here.
interface SectionHeadersFormState {
  popularPicks: { badge: string; title: string };
  bottomGrid2: { badge: string; title: string };
  bestSelling: { title: string; viewAllLabel: string };
  topSellers: { title: string; subtitle: string; viewAllLabel: string };
  flashSale: { badge: string; heading: string; subheading: string };
}

function SectionHeadersSection() {
  const qc = useQueryClient();
  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'homepage_section_headers'],
    queryFn: () => api.cms.getSetting('homepage_section_headers'),
  });
  const [val, setVal] = useState<SectionHeadersFormState>({
    popularPicks: { badge: '', title: '' },
    bottomGrid2: { badge: '', title: '' },
    bestSelling: { title: '', viewAllLabel: '' },
    topSellers: { title: '', subtitle: '', viewAllLabel: '' },
    flashSale: { badge: '', heading: '', subheading: '' },
  });
  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (!setting || inited) return;
    const v = (setting.value as SectionHeadersData | null) ?? {};
    setVal({
      popularPicks: { badge: v.popularPicks?.badge ?? '', title: v.popularPicks?.title ?? '' },
      bottomGrid2: { badge: v.bottomGrid2?.badge ?? '', title: v.bottomGrid2?.title ?? '' },
      bestSelling: {
        title: v.bestSelling?.title ?? '',
        viewAllLabel: v.bestSelling?.viewAllLabel ?? '',
      },
      topSellers: {
        title: v.topSellers?.title ?? '',
        subtitle: v.topSellers?.subtitle ?? '',
        viewAllLabel: v.topSellers?.viewAllLabel ?? '',
      },
      flashSale: {
        badge: v.flashSale?.badge ?? '',
        heading: v.flashSale?.heading ?? '',
        subheading: v.flashSale?.subheading ?? '',
      },
    });
    setInited(true);
  }, [setting, inited]);

  const upsert = useMutation({
    mutationFn: () => api.cms.upsertSetting('homepage_section_headers', val),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-setting', 'homepage_section_headers'] });
      void qc.invalidateQueries({
        queryKey: ['website-public-setting', 'homepage_section_headers'],
      });
      usePreviewStore.getState().bump();
    },
  });

  if (isLoading) return <Spinner size="lg" />;

  return (
    <SectionShell
      title="Section Headers"
      description="Override per-section titles, badges, and subheadings. Leave a field blank to keep the storefront default."
      saving={upsert.isPending}
      onSave={() => upsert.mutate()}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border-border/60 bg-surface-subtle/20 space-y-3 rounded-xl border p-4">
          <h4 className="text-ink text-[13px] font-bold">Popular Picks (Trending Now)</h4>
          <TextField
            label="Badge"
            value={val.popularPicks.badge ?? ''}
            onChange={(v) => setVal({ ...val, popularPicks: { ...val.popularPicks, badge: v } })}
            placeholder="🔥 Trending Now"
          />
          <TextField
            label="Title"
            value={val.popularPicks.title ?? ''}
            onChange={(v) => setVal({ ...val, popularPicks: { ...val.popularPicks, title: v } })}
            placeholder="Popular Picks"
          />
        </div>
        <div className="border-border/60 bg-surface-subtle/20 space-y-3 rounded-xl border p-4">
          <h4 className="text-ink text-[13px] font-bold">Bottom Grid 2 (Fresh Picks)</h4>
          <TextField
            label="Badge"
            value={val.bottomGrid2.badge ?? ''}
            onChange={(v) => setVal({ ...val, bottomGrid2: { ...val.bottomGrid2, badge: v } })}
            placeholder="✨ Fresh Picks"
          />
          <TextField
            label="Title"
            value={val.bottomGrid2.title ?? ''}
            onChange={(v) => setVal({ ...val, bottomGrid2: { ...val.bottomGrid2, title: v } })}
            placeholder="More to Explore"
          />
        </div>
        <div className="border-border/60 bg-surface-subtle/20 space-y-3 rounded-xl border p-4">
          <h4 className="text-ink text-[13px] font-bold">Best Selling</h4>
          <TextField
            label="Title"
            value={val.bestSelling.title ?? ''}
            onChange={(v) => setVal({ ...val, bestSelling: { ...val.bestSelling, title: v } })}
            placeholder="Best Selling"
          />
          <TextField
            label="View-all link label"
            value={val.bestSelling.viewAllLabel ?? ''}
            onChange={(v) =>
              setVal({ ...val, bestSelling: { ...val.bestSelling, viewAllLabel: v } })
            }
            placeholder="View all →"
          />
        </div>
        <div className="border-border/60 bg-surface-subtle/20 space-y-3 rounded-xl border p-4">
          <h4 className="text-ink text-[13px] font-bold">Top Sellers</h4>
          <TextField
            label="Title"
            value={val.topSellers.title ?? ''}
            onChange={(v) => setVal({ ...val, topSellers: { ...val.topSellers, title: v } })}
            placeholder="Top Sellers"
          />
          <TextField
            label="Subtitle"
            value={val.topSellers.subtitle ?? ''}
            onChange={(v) => setVal({ ...val, topSellers: { ...val.topSellers, subtitle: v } })}
            placeholder="Trusted stores from our marketplace"
          />
          <TextField
            label="View-all link label"
            value={val.topSellers.viewAllLabel ?? ''}
            onChange={(v) => setVal({ ...val, topSellers: { ...val.topSellers, viewAllLabel: v } })}
            placeholder="View all →"
          />
        </div>
        <div className="border-border/60 bg-surface-subtle/20 space-y-3 rounded-xl border p-4 md:col-span-2">
          <h4 className="text-ink text-[13px] font-bold">Flash Sale Banner</h4>
          <TextField
            label="Badge"
            value={val.flashSale.badge ?? ''}
            onChange={(v) => setVal({ ...val, flashSale: { ...val.flashSale, badge: v } })}
            placeholder="⚡ LIMITED FLASH OFFER"
          />
          <TextField
            label="Heading"
            value={val.flashSale.heading ?? ''}
            onChange={(v) => setVal({ ...val, flashSale: { ...val.flashSale, heading: v } })}
            placeholder="Gharghuti Spices & Sweets Flash Sale"
          />
          <TextField
            label="Subheading"
            value={val.flashSale.subheading ?? ''}
            onChange={(v) => setVal({ ...val, flashSale: { ...val.flashSale, subheading: v } })}
            placeholder="Limited time offer!"
          />
        </div>
      </div>
    </SectionShell>
  );
}

// ── Top-level page ──────────────────────────────────────────────────────────────

export function HomepageContentPage() {
  return (
    <div className="grid gap-6 pb-8 xl:grid-cols-[1fr_minmax(380px,460px)]">
      {/* LEFT — editor sections */}
      <div className="space-y-6">
        <div className="mb-2 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-ink text-[22px] font-extrabold tracking-tight">Homepage Content</h1>
            <p className="text-ink-muted mt-1 text-[13px] font-medium">
              Manage all admin-controlled blocks on the storefront homepage. Each section saves
              independently; leave blank fields where you want the storefront default.
            </p>
          </div>
        </div>

        <PromoCardsSection />
        <EnergyCardsSection />
        <TopSellersSection />
        <SectionHeadersSection />
      </div>

      {/* RIGHT — live storefront iframe; auto-reloads after each sub-section save. */}
      <div className="hidden xl:block">
        <LivePreviewPanel />
      </div>
    </div>
  );
}
